import { useSignal } from "@preact/signals";
import type { PageArgs } from "bunfx";
import { navigateTo } from "bunfx";
import { AuthLayout } from "../components/layout";
import {
  Button,
  Form,
  type FormErrors,
  Input,
  Password,
} from "../components/ui";
import { usePageTitle } from "../hooks";
import { rpc } from "../rpc";

type FormState = {
  errors: Record<string, string>;
  loading: boolean;
};

type LoaderData = {
  redirectTo: string;
};

export async function load({
  searchParams,
}: {
  searchParams: Record<string, string>;
}): Promise<LoaderData> {
  return {
    redirectTo: searchParams.redirectto || "/",
  };
}

export function Page({ state: loaderState }: PageArgs<typeof load>) {
  usePageTitle("Log In");
  const { redirectTo } = loaderState.value;
  const state = useSignal<FormState>({ errors: {}, loading: false });

  // FormErrors-compatible lens for Form component
  const errors = {
    get value() {
      return state.value.errors;
    },
    set value(v: Record<string, string>) {
      state.value = { ...state.value, errors: v };
    },
  } as FormErrors;

  async function handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    state.value = { errors: {}, loading: true };

    try {
      await rpc.auth.login({ email, password });
      navigateTo(redirectTo);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "message" in err) {
        state.value = {
          errors: { form: (err as Error).message },
          loading: false,
        };
      } else {
        state.value = {
          errors: { form: "Something went wrong" },
          loading: false,
        };
      }
    }
  }

  return (
    <AuthLayout title="Sign in to Pulse" subtitle="Welcome back">
      <Form errors={errors} onSubmit={handleSubmit} class="flex flex-col gap-4">
        {errors.value.form && (
          <div class="px-3 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 text-sm font-medium">
            {errors.value.form}
          </div>
        )}

        <Input
          label="Email"
          name="email"
          type="email"
          autocomplete="email"
          autofocus
          required
        />

        <Password
          label="Password"
          name="password"
          autocomplete="current-password"
          required
        />

        <Button type="submit" loading={state.value.loading} class="w-full mt-1">
          Sign in
        </Button>
      </Form>

      <div class="mt-4 text-center">
        <a
          href="/forgotpassword"
          class="text-sm text-text-muted hover:text-text-secondary transition-colors"
        >
          Forgot password?
        </a>
      </div>

      <div class="mt-5 pt-5 border-t border-border-muted text-center">
        <p class="text-sm text-text-muted">
          Don't have an account?{" "}
          <a
            href="/signup"
            class="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
          >
            Sign up
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
